import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ImagePlus, Loader2, Save, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { FieldError } from "@/components/FieldError";
import { getErrorMessage, getFieldErrors } from "@/lib/errors";

const FIELDS = ["name", "gstin", "pan", "address", "state", "phone", "email", "website"];
const toForm = (c) => Object.fromEntries(FIELDS.map((f) => [f, c?.[f] ?? ""]));

/**
 * The company's profile: legal name, GSTIN, PAN, address, state, phone, email and website.
 * Used on Organization Settings and on the Company Profile page. Saves to the company record, which is what
 * documents and reports read from.
 */
export function CompanyDetailsForm({ company, onSaved, onLogoChanged, submitLabel = "Save details" }) {
  const { refresh } = useCompany() || {};
  const [form, setForm] = useState(() => toForm(company));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const fileRef = useRef(null);

  // The logo is behind the sign-in, so fetch it with the token and show it from a temporary local address.
  useEffect(() => {
    let url = null; let cancelled = false;
    if (company?.logo_updated_at) {
      api.get(`/masters/companies/${company.id}/logo`, { responseType: "blob" })
        .then(({ data }) => { if (!cancelled) { url = URL.createObjectURL(data); setLogoUrl(url); } })
        .catch(() => { if (!cancelled) setLogoUrl(null); });
    } else {
      setLogoUrl(null);
    }
    return () => { cancelled = true; if (url) URL.revokeObjectURL(url); };
  }, [company?.id, company?.logo_updated_at]);

  const uploadLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setLogoBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post(`/masters/companies/${company.id}/logo`, body);
      toast.success("Logo saved");
      refresh?.();
      onLogoChanged?.(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLogoBusy(false);
      e.target.value = "";
    }
  };

  const removeLogo = async () => {
    setLogoBusy(true);
    try {
      await api.delete(`/masters/companies/${company.id}/logo`);
      toast.success("Logo removed");
      refresh?.();
      onLogoChanged?.({ ...company, logo_updated_at: null });
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLogoBusy(false);
    }
  };

  useEffect(() => { setForm(toForm(company)); setErrors({}); }, [company?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const set = (field) => (e) => { setForm({ ...form, [field]: e.target.value }); if (errors[field]) setErrors({ ...errors, [field]: undefined }); };

  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSaving(true);
    try {
      const payload = Object.fromEntries(FIELDS.map((f) => [f, f === "name" ? form[f].trim() : (form[f].trim() || null)]));
      const { data } = await api.patch(`/masters/companies/${company.id}`, payload);
      toast.success("Details saved");
      refresh?.();
      onSaved?.(data);
    } catch (err) {
      setErrors(getFieldErrors(err));
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const field = (name, label, props = {}) => (
    <div className={props.wide ? "sm:col-span-2" : ""}>
      <Label htmlFor={`company-${name}`}>{label}</Label>
      <Input id={`company-${name}`} data-testid={`company-${name}-input`} value={form[name]} onChange={set(name)} className="mt-1.5" {...props.input} />
      <FieldError error={errors[name]} />
    </div>
  );

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="company-details-form">
      <div className="flex flex-wrap items-center gap-4 rounded-lg border border-dashed border-border p-3" data-testid="company-logo-block">
        <div className="grid h-16 w-40 shrink-0 place-items-center overflow-hidden rounded-md bg-secondary/50">
          {logoUrl ? <img src={logoUrl} alt="Company logo" className="max-h-16 max-w-40 object-contain" data-testid="company-logo-preview" /> : <span className="text-xs text-muted-foreground">No logo</span>}
        </div>
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-foreground">Logo</p>
          <p className="text-xs text-muted-foreground">PNG or JPEG, up to 2 MB. It is printed at the top of your documents.</p>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept="image/png,image/jpeg" className="hidden" onChange={uploadLogo} data-testid="company-logo-input" />
            <Button type="button" size="sm" variant="outline" disabled={logoBusy} onClick={() => fileRef.current?.click()} data-testid="company-logo-upload-button">
              {logoBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />} {company?.logo_updated_at ? "Change logo" : "Upload logo"}
            </Button>
            {company?.logo_updated_at && <Button type="button" size="sm" variant="ghost" disabled={logoBusy} onClick={removeLogo} data-testid="company-logo-remove-button"><Trash2 className="h-3.5 w-3.5" /> Remove</Button>}
          </div>
        </div>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        {field("name", "Legal name", { wide: true, input: { required: true, minLength: 2 } })}
        {field("gstin", "GSTIN", { input: { placeholder: "27AAPFU0939F1ZV", maxLength: 15 } })}
        {field("pan", "PAN", { input: { placeholder: "AAPFU0939F", maxLength: 10 } })}
        <div className="sm:col-span-2">
          <Label htmlFor="company-address">Address</Label>
          <Textarea id="company-address" data-testid="company-address-input" value={form.address} onChange={set("address")} className="mt-1.5" rows={3} />
          <FieldError error={errors.address} />
        </div>
        {field("state", "State")}
        {field("phone", "Phone", { input: { placeholder: "+91 98765 43210", inputMode: "tel" } })}
        {field("email", "Email", { input: { placeholder: "accounts@yourcompany.com", inputMode: "email" } })}
        {field("website", "Website", { input: { placeholder: "www.yourcompany.com" } })}
      </div>
      <Button type="submit" disabled={saving} data-testid="save-company-details-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} {submitLabel}
      </Button>
    </form>
  );
}
